'use client';

import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import styles from './AstronautArtwork.module.scss';

const generateStars = (num: number) => {
  return Array.from({ length: num }).map((_, i) => (
    <div key={i} className={styles.star} />
  ));
};

const generateCraters = () => {
  return Array.from({ length: 6 }).map((_, i) => (
    <div
      key={i + 1}
      className={classNames(styles.crater, styles[`crater${i + 1}`])}
    />
  ));
};

const Antena = () => (
  <>
    <div className={styles.earDown} />
    <div className={styles.earUp} />
    <div className={styles.antena} />
    <div className={styles.antenaTip} />
  </>
);

interface Cloud {
  id: number;
  y: number;
  duration: number;
  size: number;
  popped?: boolean;
  message?: string;
  popX?: number;
  popY?: number;
}

interface AstronautArtworkProps {
  /** Controls the artwork size. 1 = default (355px circle), 1.4 = 40% bigger, etc. */
  size?: number;
  mouseTracking?: boolean;
  messages?: string[];
  children?: React.ReactNode;
  leftDigit?: string;
  rightDigit?: string;
}

export const AstronautArtwork = ({
  size = 1,
  mouseTracking = true,
  messages = [],
  children,
  leftDigit = '5',
  rightDigit = '3'
}: AstronautArtworkProps) => {
  const ohRef = useRef<HTMLDivElement>(null);
  const [clouds, setClouds] = useState<Cloud[]>([]);
  const cloudIdRef = useRef(0);

  useEffect(() => {
    if (!mouseTracking) return;
    const oh = ohRef.current;
    if (!oh) return;

    const handleMouseMove = (event: MouseEvent) => {
      const domainX = [0, window.innerWidth];
      const domainY = [0, window.innerHeight];
      const range = [-10, 10];

      const translate = {
        x:
          range[0] +
          ((event.clientX - domainX[0]) * (range[1] - range[0])) /
            (domainX[1] - domainX[0]),
        y:
          range[0] +
          ((event.clientY - domainY[0]) * (range[1] - range[0])) /
            (domainY[1] - domainY[0])
      };

      oh.style.animation = 'none';
      oh.style.transform = `translate(${translate.x}px, ${translate.y}px)`;
    };

    const handleMouseLeave = () => {
      oh.style.animation = 'floating 3s linear infinite';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [mouseTracking]);

  // Spawn clouds
  useEffect(() => {
    if (messages.length === 0) return;

    const spawnCloud = () => {
      const id = cloudIdRef.current++;
      const cloud: Cloud = {
        id,
        y: 10 + Math.random() * 70,
        duration: 20 + Math.random() * 15,
        size: 60 + Math.random() * 40
      };
      setClouds((prev) => [...prev, cloud]);

      // Remove cloud after it exits screen
      setTimeout(
        () => {
          setClouds((prev) => prev.filter((c) => c.id !== id));
        },
        cloud.duration * 1000 + 500
      );
    };

    // Spawn initial clouds staggered
    spawnCloud();
    const t1 = setTimeout(spawnCloud, 2000);
    const t2 = setTimeout(spawnCloud, 4500);

    const interval = setInterval(
      () => {
        spawnCloud();
      },
      3000 + Math.random() * 3000
    );

    return () => {
      clearInterval(interval);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [messages.length]);

  const popCloud = (id: number, x: number, y: number) => {
    if (messages.length === 0) return;
    const message = messages[Math.floor(Math.random() * messages.length)];
    setClouds((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, popped: true, message, popX: x, popY: y } : c
      )
    );
    setTimeout(() => {
      setClouds((prev) => prev.filter((c) => c.id !== id));
    }, 2500);
  };

  return (
    <div
      className={styles.main}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundImage: 'none',
        borderRadius: 'inherit',
        pointerEvents: 'none'
      }}
    >
      <div
        className={classNames(styles.stars, styles.main)}
        style={{
          background: 'none',
          width: '100%',
          height: '100%',
          pointerEvents: 'none'
        }}
      >
        {generateStars(80)}
      </div>

      {/* Clouds layer */}
      {clouds.map((cloud) =>
        cloud.popped ? (
          <div
            key={cloud.id}
            className="text-textSecondary"
            style={{
              position: 'fixed',
              left: cloud.popX,
              top: cloud.popY,
              transform: 'translate(-50%, -50%)',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              animation: 'popMessage 2.5s ease-out forwards',
              textAlign: 'center',
              letterSpacing: '0.5px',
              zIndex: 4,
              pointerEvents: 'none'
            }}
          >
            {cloud.message}
          </div>
        ) : (
          <div
            key={cloud.id}
            onClick={(e) => popCloud(cloud.id, e.clientX, e.clientY)}
            style={{
              position: 'absolute',
              top: `${cloud.y}%`,
              left: '-80px',
              cursor: 'pointer',
              pointerEvents: 'auto',
              animation: `cloudDrift ${cloud.duration}s linear forwards`
            }}
          >
            <svg
              className="fill-background-secondary"
              width={cloud.size * 1.5}
              height={cloud.size * 0.6}
              viewBox="0 0 150 60"
            >
              <ellipse cx="75" cy="45" rx="65" ry="15" />
              <ellipse cx="45" cy="32" rx="25" ry="22" />
              <ellipse cx="75" cy="28" rx="28" ry="25" />
              <ellipse cx="105" cy="32" rx="25" ry="22" />
              <ellipse cx="60" cy="35" rx="20" ry="18" />
              <ellipse cx="90" cy="35" rx="20" ry="18" />
            </svg>
          </div>
        )
      )}

      <style>{`
        @keyframes cloudDrift {
          from { transform: translateX(0); }
          to { transform: translateX(calc(100vw + 160px)); }
        }
        @keyframes popMessage {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
          15% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          70% { opacity: 1; transform: translate(-50%, -50%) translateY(-10px); }
          100% { opacity: 0; transform: translate(-50%, -50%) translateY(-30px); }
        }
      `}</style>

      <div
        className={styles.center}
        style={{ '--size-base': size } as React.CSSProperties}
      >
        <div className={classNames(styles.circle, styles.circleMain)}>
          <span className={classNames(styles.four, styles.four1)}>
            {leftDigit}
          </span>
          <span className={classNames(styles.four, styles.four2)}>
            {rightDigit}
          </span>
          <div
            ref={ohRef}
            className={classNames(styles.circle, styles.oh)}
            style={{ zIndex: 2 }}
          >
            <div className={styles.astronaut}>
              <div className={styles.astronautBackpack} />
              <div
                className={classNames(
                  styles.astronautAntena,
                  styles.antenaLeft
                )}
              >
                <Antena />
              </div>
              <div
                className={classNames(
                  styles.astronautAntena,
                  styles.antenaRight
                )}
              >
                <Antena />
              </div>
              <div className={styles.astronautHelmet}>
                <div className={styles.astronautGlass} />
                <div className={classNames(styles.glow, styles.glow1)} />
                <div className={classNames(styles.glow, styles.glow2)} />
              </div>
              <div className={styles.astronautBody} />
            </div>

            <svg
              className={`${styles.astronautWire} text-textSecondary`}
              viewBox="-9 -9 259 823"
              fill="none"
            >
              <path
                d="M241 0c-24 54-30 113-78 148S63 159 27 215c-35 55 32 102 73 141s103 94 98 166c-6 97-169 66-192 157-10 43-8 84 9 126"
                fill="none"
                strokeWidth="6"
                stroke="currentColor"
              />
            </svg>

            <div className={classNames(styles.circle, styles.planet)}>
              <div className={styles.craters}>{generateCraters()}</div>
            </div>
            <div className={styles.astronautHands}>
              <div className={classNames(styles.astronautHand, styles.wrench)}>
                <svg className={styles.astronautWire} viewBox="-1 -1 602 802">
                  <path
                    d="m217.411,167.068l-70.305,-70.301l9.398,-35.073c0.693,-2.588 -0.046,-5.35 -1.94,-7.245l-52.253,-52.252c-1.895,-1.895 -4.656,-2.635 -7.245,-1.941l-39.254,10.518c-2.588,0.694 -4.609,2.715 -5.303,5.303c-0.693,2.588 0.046,5.35 1.941,7.245l39.259,39.262l-6.364,23.756l-23.751,6.363l-39.263,-39.26c-1.895,-1.894 -4.655,-2.635 -7.245,-1.94c-2.588,0.693 -4.61,2.715 -5.303,5.303l-10.517,39.253c-0.693,2.588 0.046,5.349 1.941,7.244l52.249,52.255c1.895,1.895 4.657,2.635 7.245,1.941l35.076,-9.4l70.302,70.306c6.854,6.854 15.968,10.628 25.662,10.629l0.001,0c9.695,0 18.81,-3.776 25.665,-10.631c14.153,-14.151 14.156,-37.178 0.004,-51.335zm-25.671,46.966l-88.419,-79.603c-1.425,-1.425 -3.34,-2.197 -5.304,-2.197c-1.964,0 -1.299,0.083 -1.941,0.255l95.664,81.545z"
                    fill="#B2B2B2"
                  />
                </svg>
              </div>
              <div
                className={classNames(styles.astronautHand, styles.handLeft)}
              >
                <svg viewBox="0 0 35 75">
                  <path
                    d="M30.23 17.209c-7.925 5.118-11.657 12.786-11.226 22.975-7.113.934-12.948 4.345-18.44 5.117C-1.951 26.539 3.92 9.346 18.635 1.369 30.66-4.39 39.53 9.398 30.23 17.209z"
                    fill="#D2D2D2"
                  />
                  <g fill="none" stroke="#999" strokeLinecap="round">
                    <path d="M11.78 6.977c7.983.129 13.547 3.968 16.308 11.111M4.67 17.161c7.307-.379 13.1 1.924 17.93 6.94" />
                    <path
                      d="M.816 31.334c6.439-2.441 12.295-1.746 18.149 2.488"
                      strokeLinejoin="round"
                    />
                  </g>
                  <g fill="#e0e0e0">
                    <path d="M7.721 37.171c5.875-1.994 12.264 1.156 14.258 7.031l1.218 3.588c1.995 5.875-1.156 12.264-7.03 14.258-5.875 1.995-12.264-1.156-14.259-7.031L.69 51.429c-1.994-5.875 1.156-12.263 7.031-14.258z" />
                    <path d="M7.829 38.159c5.794-1.967 12.094 1.14 14.061 6.934l5.044 14.855c1.967 5.794-1.14 12.095-6.934 14.062-5.794 1.967-12.095-1.14-14.062-6.934L.895 52.221c-1.967-5.794 1.14-12.095 6.934-14.062z" />
                    <path d="M16.863 39.472l12.879 7.384a3.876 3.876 0 0 1 1.433 5.287 3.875 3.875 0 0 1-5.286 1.433l-12.878-7.384a3.874 3.874 0 0 1-1.434-5.286 3.874 3.874 0 0 1 5.286-1.434z" />
                  </g>
                </svg>
              </div>
              <div
                className={classNames(styles.astronautHand, styles.handRight)}
              >
                <svg viewBox="0 0 35 75">
                  <path
                    d="M30.23 17.209c-7.925 5.118-11.657 12.786-11.226 22.975-7.113.934-12.948 4.345-18.44 5.117C-1.951 26.539 3.92 9.346 18.635 1.369 30.66-4.39 39.53 9.398 30.23 17.209z"
                    fill="#D2D2D2"
                  />
                  <g fill="none" stroke="#999" strokeLinecap="round">
                    <path d="M11.78 6.977c7.983.129 13.547 3.968 16.308 11.111M4.67 17.161c7.307-.379 13.1 1.924 17.93 6.94" />
                    <path
                      d="M.816 31.334c6.439-2.441 12.295-1.746 18.149 2.488"
                      strokeLinejoin="round"
                    />
                  </g>
                  <g fill="#e0e0e0">
                    <path d="M7.721 37.171c5.875-1.994 12.264 1.156 14.258 7.031l1.218 3.588c1.995 5.875-1.156 12.264-7.03 14.258-5.875 1.995-12.264-1.156-14.259-7.031L.69 51.429c-1.994-5.875 1.156-12.263 7.031-14.258z" />
                    <path d="M7.829 38.159c5.794-1.967 12.094 1.14 14.061 6.934l5.044 14.855c1.967 5.794-1.14 12.095-6.934 14.062-5.794 1.967-12.095-1.14-14.062-6.934L.895 52.221c-1.967-5.794 1.14-12.095 6.934-14.062z" />
                    <path d="M16.863 39.472l12.879 7.384a3.876 3.876 0 0 1 1.433 5.287 3.875 3.875 0 0 1-5.286 1.433l-12.878-7.384a3.874 3.874 0 0 1-1.434-5.286 3.874 3.874 0 0 1 5.286-1.434z" />
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {children}
    </div>
  );
};
